/**
 * Proposal types — the "response" side of the BTX data model.
 *
 * These mirror the request-side types (RfpData → Proposal, RfpCoverage → ProposalCoverage, etc.)
 * but represent what a carrier sends BACK in response to an RFP.
 *
 * Key differences from the request side:
 * - Proposal is scoped to a single carrier (CarrierID, CarrierName)
 * - ProposalCoverage links back to its RFP coverage via rfpCoverageId
 * - ProposalPlanGroup adds SBS display fields (networkMode, planRole, sequencePosition)
 * - ProposalPlan reuses the same benefit structures (RfpBenefitPlan) as the request side
 * - Rate types share the same RateSchedule / Rate interfaces
 *
 * Backend domain: src/apps/backend/btx/internal/domain/proposal.go
 */

import type { RiderCharges } from "@platform/btx-rates";
import type { CoverageTypeEnum } from "@platform/types";
import type {
  Rate,
  RateSchedule,
  RfpBenefitPlan,
} from "@platform/plan-grid-core";

// ============================================================================
// Enums
// ============================================================================

/** Proposal-level status values. */
export enum ProposalStatusEnum {
  PENDING = "pending",
  RECEIVED = "received",
  DECLINED = "declined",
}

/** Coverage-level status within a proposal. */
export enum ProposalCoverageStatusEnum {
  QUOTED = "quoted",
  DECLINED = "declined",
  PENDING = "pending",
}

/** Network mode for the SBS comparison grid. */
export enum NetworkModeEnum {
  AGNOSTIC = "agnostic",
  DUAL = "dual",
  TRI = "tri",
}

/** The role a plan group plays in the SBS comparison. */
export enum PlanRoleEnum {
  QUOTED = "quoted",
  CURRENT = "current",
  REQUESTED = "requested",
}

// ============================================================================
// Proposal (root) — mirrors RfpData
// ============================================================================

/** A carrier's structured response to an RFP. One RFP → many Proposals. */
export interface Proposal {
  id: string;
  createdAt: string;
  updatedAt: string;

  rfpId: string;
  carrierId: string;
  carrierName: string;
  status: ProposalStatusEnum;
  rfpVersion: number;
  isActiveVersion: boolean;
  /**
   * Requote review state (ADR-031): a `pending` requote is an incoming version
   * awaiting analyst action (Replace/Disregard); `actioned` covers the active
   * version and archived historical predecessors. Absent on legacy payloads.
   */
  requoteReviewState?: "pending" | "actioned";
  receivedAt?: string;
  rfpqProposalId?: string;
  /** How the proposal was created (ADR-024). */
  origin?: "carrier" | "analyst" | "document";
  /** Source document id for `origin = "document"` proposals (one column per document). */
  documentId?: string;
  /** Uploaded filename for `origin = "document"` proposals, shown in the Source Document row. */
  documentName?: string;

  coverages: ProposalCoverage[];
}

// ============================================================================
// ProposalCoverage — mirrors RfpCoverage
// ============================================================================

/** A single line of coverage within a carrier's proposal. */
export interface ProposalCoverage {
  id: string;
  createdAt: string;
  updatedAt: string;

  coverageType: CoverageTypeEnum;
  /** Links back to the RFP coverage for SBS join. */
  rfpCoverageId?: string;
  status: ProposalCoverageStatusEnum;
  statusReason?: string;
  /**
   * Product plan types the analyst requested for this coverage. `null` means the whole
   * coverage was requested; an empty array means it was not requested at all. Only dental
   * carries a non-empty list today.
   */
  requestedProductPlanTypes: string[] | null;
  planGroups: ProposalPlanGroup[];
}

// ============================================================================
// ProposalPlanGroup — mirrors BenefitPlanGroup + SBS display fields
// ============================================================================

/** A carrier's plan group offering within a proposal coverage. */
export interface ProposalPlanGroup {
  id: string;
  /**
   * The Nth quote this carrier has sent for the RFP-requested plan this group answers — the
   * V1/V2 chip on that plan's SBS page (CD-1767). Scoped per
   * (rfp, carrier, rfpBenefitPlanGroupId), so each requested plan of a coverage numbers
   * independently and requoting one does not advance another. Independent of
   * `Proposal.rfpVersion`. Absent for an unquoted placeholder, and for an unmapped group,
   * which sits on the `__unmapped__` page and has no requested plan to number within.
   */
  carrierQuoteVersion?: number;
  createdAt: string;
  updatedAt: string;

  /**
   * Whether this plan group is the active (live-on-the-board) version of the
   * requested plan it answers, as opposed to an un-actioned pending requote or
   * actioned quote-history version (ADR-031). Same grain as `carrierQuoteVersion`:
   * a Replace/Disregard/Restore acts on one requested plan without touching
   * another sharing the same proposal row (CD-1792 follow-up). Absent for an
   * unquoted placeholder or unmapped group — those fall back to the owning
   * `Proposal.isActiveVersion`.
   */
  isActiveVersion?: boolean;
  /** Analyst review state for this plan group's requested plan (ADR-031, CD-1792
   * follow-up). Absent for an unquoted placeholder or unmapped group — those fall
   * back to the owning `Proposal.requoteReviewState`. */
  requoteReviewState?: "pending" | "actioned";

  /**
   * When the analyst deleted this plan group (CD-89 AC1). Only the `all` read scope
   * returns deleted groups, for Quote History; absent under every other scope.
   *
   * Deletion is only permitted on the active version, so a deleted group still reports
   * `isActiveVersion: true`. Anything deriving active/pending/historical state must check
   * this first, or a deleted quote reads as the live one.
   */
  deletedAt?: string;

  /** Links to the RFP BenefitPlanGroup this quote is responding to. */
  rfpBenefitPlanGroupId?: string;
  /**
   * True for synthesized "awaiting quote" placeholder groups (ADR-024). These
   * are never persisted; editing one materializes a real analyst proposal.
   */
  isPlaceholder?: boolean;
  productType: string;
  position: number;
  /** Display position in the SBS comparison grid. */
  sequencePosition: number;
  /** Network column layout: agnostic (single), dual, tri. */
  networkMode: NetworkModeEnum;
  /** Role in comparison: quoted, current, or requested. */
  planRole: PlanRoleEnum;
  isCurrent: boolean;
  nickname?: string;
  /**
   * Analyst-entered footnote shown at the bottom of this carrier's SBS rates
   * table; one per carrier group, carried through to the marketing-presentation
   * export.
   */
  footnote?: string;
  size: number;

  plans: ProposalPlan[];

  /** Backend-computed total monthly premium for the group (ADR-026). */
  totalMonthlyPremium?: number;
  /** Backend-computed Total Annual Premium for the group (ADR-026). */
  totalAnnualPremium?: number;

  /**
   * True when the group's total enrolled lives or volume, as the carrier quoted them, differs
   * from the Current plan's by 9% or more. Set only on the Quotes read.
   */
  enrollmentGroupFlagged?: boolean;
}

// ============================================================================
// ProposalPlan — mirrors RfpBenefitPlan (shares same benefit types)
// ============================================================================

/** An individual quoted plan within a carrier's proposal. */
export interface ProposalPlan {
  id: string;
  createdAt: string;
  updatedAt: string;

  /** Links to the RFP BenefitPlan this quote is responding to. */
  rfpBenefitPlanId?: string;
  /** 1-based slot within the plan group; the materialize payload's planPosition. */
  position?: number;
  /**
   * True when this plan was synthesized at read time and never persisted:
   * a slot of an "awaiting quote" placeholder group, or an empty pad slot on a
   * real under-quoted group (CD-1612 partial fill). Editing a pad slot routes
   * to the add-plan-to-group endpoint rather than a plan PATCH.
   */
  isPlaceholder?: boolean;
  type?: string;
  optionName?: string;
  name?: string;
  benefitClassAvailability: string[];

  /**
   * Plan-level fields (siblings of `benefits`), mirroring the request side. These
   * are returned by the API and edited via benefitPlanLevel grid cells; they must
   * be carried through the adapter or edits round-trip to empty.
   */
  fundingType?: RfpBenefitPlan["fundingType"];
  participationRequirements?: RfpBenefitPlan["participationRequirements"];
  rateGuarantee?: RfpBenefitPlan["rateGuarantee"];
  rateCap?: RfpBenefitPlan["rateCap"];
  eligibilityClasses?: RfpBenefitPlan["eligibilityClasses"];

  /**
   * Same benefit union type as the request side (RfpBenefitPlan.benefits).
   * This is the key parity point — both sides use identical benefit structures.
   */
  benefits?: RfpBenefitPlan["benefits"];
  /**
   * Quote-side rate schedules. These mirror request-side rate schedules and
   * are the source of truth for presentation/quote-data rate displays.
   */
  rateSchedules?: ProposalRateSchedule[];

  /** Backend-computed monthly premium for this plan (ADR-026). */
  monthlyPremium?: number;
  /** Backend-computed annual premium for this plan (ADR-026). */
  annualPremium?: number;

  /**
   * Flat rider charges in USD per covered relationship (Critical Illness).
   * Absent when no charge is set. Edited via PATCH /proposal-plans/:id.
   */
  riderCharges?: RiderCharges;
}

// ============================================================================
// ProposalRateSchedule / ProposalRate — mirrors RateSchedule / Rate
// ============================================================================

/** A rate schedule attached to a proposal plan. */
export interface ProposalRateSchedule extends RateSchedule {
  id: string;
  createdAt: string;
  updatedAt: string;

  proposalPlanId: string;
  position: number;
  rates: ProposalRate[];

  /**
   * The analyst's choice between the Current plan's enrollment and the carrier's, made once for
   * the whole plan group. Null until one is made. Set only on the Quotes read.
   */
  enrollmentDecision?: "kept_current" | "used_carrier" | null;
  enrollmentDecidedBy?: string;
  enrollmentDecidedAt?: string;
  /**
   * True when this schedule, or its plan group, is flagged and no decision has been made.
   * The flags in the grid show while this is true.
   */
  enrollmentNeedsDecision?: boolean;
}

/** An individual rate row within a proposal rate schedule. */
export interface ProposalRate extends Rate {
  id: string;
  createdAt: string;
  updatedAt: string;

  proposalRateScheduleId: string;
  position: number;

  /** Lives and volume the carrier quoted; `numberOfLives` and `volume` show Current's where the copy applied. */
  carrierNumberOfLives?: number;
  carrierVolume?: number;
  /** The Current plan's lives and volume for this rate. Set only on the Quotes read. */
  currentNumberOfLives?: number;
  currentVolume?: number;
  /** True when the carrier's lives or volume differ from Current's by 9% or more. */
  enrollmentVarianceFlagged?: boolean;
}
